import { Injectable } from '@angular/core';
import { switchMap } from 'rxjs/operators';
import { Observable, throwError, of } from 'rxjs';

import { ApiService } from './api.service';
import type { ReferralStats, ReferralReward, ReferralTransaction, ReferrerDetails } from '../models/referral';
import type { Pagination } from '../models/pagination';

@Injectable({
  providedIn: 'root'
})
export class ReferralService {

  constructor(private api: ApiService) { }

  getStats(): Observable<ReferralStats> {
    return this.api.get(`{
        referralStats{
          link
          outstandingReward{amount currency}
          minRewardPayment{amount currency}
          rewardSteps{
            reward
            fromInterval
            fromIntervalInMonths
            toInterval
            toIntervalInMonths
          }
        }
      }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['referralStats']);
      }),
      this.api.unifyErrors()
    );
  }

  getRewards(first = 10, after: string = null): Observable<Pagination<ReferralReward>> {
    return this.api.get(`{
        referralStats{
          rewards(
            first: ${first}
            ${after ? ', after: "' + after + '"' : ''}
          ){
            edges{
              node{
                dateDone
                amount{amount currency}
                statusNote
              }
              cursor
            }
            pageInfo{
              startCursor
              endCursor
              hasNextPage
            }
            totalCount
          }
        }
      }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['referralStats']['rewards']);
      }),
      this.api.unifyErrors()
    );
  }

  getTransactions(first = 10, after: string = null): Observable<Pagination<ReferralTransaction>> {
    return this.api.get(`{
        referralStats{
          transactions(
            first: ${first}
            ${after ? ', after: "' + after + '"' : ''}
          ){
            edges{
              node{
                dateCreated
                rewardPercent
                rewardFullPrecisionAmount
                rewardFullPrecisionCurrency
                rewardAmount{amount currency}
                paid
              }
              cursor
            }
            pageInfo{
              startCursor
              endCursor
              hasNextPage
            }
            totalCount
          }
        }
      }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['referralStats']['transactions']);
      }),
      this.api.unifyErrors()
    );
  }

  sendInvitationEmails(emails: string[]): Observable<boolean> {
    return this.api.get(`mutation{
      sendReferralInvitationEmails(
        emails: "${emails}"
      ){
        ... on GeneralResponse{
          success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['sendReferralInvitationEmails'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  getReferrerDetails(referralId: string): Observable<ReferrerDetails> {
    return this.api.get(`
      query {
        referrerDetail(referralId: "${referralId}") {
          firstName
          tags
        }
      }`,
      null,
      false
    ).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['referrerDetail']);
      }),
      this.api.unifyErrors()
    );
  }
}
